import React, { useState } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  {
    id: 'age_category',
    title_fr: 'Quelle est ta categorie?',
    subtitle_fr: 'On adapté le terrain a ta realite.',
    options: [
      { value: 'performance', label_fr: 'Performance (18-40 ans)', icon: '⚡', desc_fr: 'Dynamique · résultats · dépassement' },
      { value: 'longevite', label_fr: 'Longévité (40-90 ans)', icon: '🛡️', desc_fr: 'Vitalité · sommeil · prévention' },
    ],
  },
  {
    id: 'sex',
    title_fr: 'Ton identite de combattant(e)',
    subtitle_fr: 'Pour calibrer les recommandations.',
    options: [
      { value: 'homme', label_fr: 'Homme', icon: '♂️', desc_fr: 'Athlete Extra Miles' },
      { value: 'femme', label_fr: 'Femme', icon: '♀️', desc_fr: 'Athlete Extra Miles' },
      { value: 'autre', label_fr: 'Autre / Prefere ne pas dire', icon: '⚡', desc_fr: 'Membre Extra Miles' },
    ],
  },
  {
    id: 'main_objective',
    title_fr: 'Ta mission principale',
    subtitle_fr: 'On concentre le feu sur ta cible.',
    options: [
      { value: 'perte_poids', label_fr: 'Perte de poids radicale', icon: '🎯', desc_fr: 'Brûler les réserves et alléger l\'armure' },
      { value: 'prise_masse', label_fr: 'Prise de masse / Force', icon: '💪', desc_fr: 'Bâtir la charpente et augmenter la puissance' },
      { value: 'mobilité', label_fr: 'Mobilité / Santé articulaire', icon: '🦵', desc_fr: 'Réhabilitation, longévité et fluidité' },
      { value: 'endurance', label_fr: 'Endurance Tactique', icon: '🫀', desc_fr: 'Conditionnement cardio, préparation d\'epreuve' },
      { value: 'maintien', label_fr: 'Maintien Operationnel', icon: '⚖️', desc_fr: 'Recomposition corporelle et maintien des acquis' },
    ],
  },
];

export const TerrainProfile = ({ onComplete }) => {
  const { language } = useLanguage();
  const [step, setStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const currentStep = STEPS[step];

  const handleSelect = (value) => {
    setSelections(prev => ({ ...prev, [currentStep.id]: value }));
  };

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      try {
        await api.post('/users/terrain-profile', selections);
        toast.success(language === 'fr' ? 'Profil Terrain enregistré!' : 'Terrain Profile saved!');
        onComplete(selections);
      } catch (err) {
        toast.error(language === 'fr' ? 'Erreur' : 'Error');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const selected = selections[currentStep?.id];
  const progress = ((step + (selected ? 1 : 0)) / STEPS.length) * 100;
  const isMissionStep = currentStep?.id === 'main_objective';

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4" data-testid="terrain-profile-onboarding">
      {/* Progress bar */}
      <div className="w-full max-w-md mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">
            {language === 'fr' ? 'Reconnaissance du Terrain' : 'Terrain Recon'}
          </span>
          <span className="text-xs text-muted-foreground">{step + 1}/{STEPS.length}</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-red-600 to-amber-500"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-heading font-black uppercase tracking-tight text-foreground" data-testid={`terrain-step-${step}-title`}>
              {currentStep.title_fr}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">{currentStep.subtitle_fr}</p>
          </div>

          <div className="space-y-3">
            {currentStep.options.map(opt => (
              <motion.button
                key={opt.value}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSelect(opt.value)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selected === opt.value
                    ? 'border-red-500 bg-red-500/10 shadow-[0_0_20px_rgba(239,68,68,0.15)]'
                    : 'border-border/50 bg-card/50 hover:border-border'
                }`}
                data-testid={`terrain-option-${opt.value}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{opt.icon}</span>
                  <div className="flex-1">
                    <p className={`font-heading text-base uppercase tracking-tight ${selected === opt.value ? 'text-red-400' : 'text-foreground'}`}>
                      {opt.label_fr}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{opt.desc_fr}</p>
                  </div>
                  {selected === opt.value && <Check className="w-5 h-5 text-red-400" />}
                </div>
              </motion.button>
            ))}
          </div>

          {/* Flexibility note — only on mission step */}
          {isMissionStep && (
            <p className="text-xs text-muted-foreground text-center mt-4 italic" data-testid="terrain-flexibility-note">
              {language === 'fr'
                ? 'Le terrain évolué. Tu pourras modifier ta mission principale en tout temps dans tes parametres.'
                : 'The terrain evolves. You can change your main mission anytime in your settings.'}
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="w-full max-w-md mt-8 flex gap-3">
        {step > 0 && (
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            className="flex-1 font-heading uppercase tracking-wider"
            data-testid="terrain-back-btn"
          >
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
        )}
        <Button
          onClick={handleNext}
          disabled={!selected || submitting}
          className="flex-1 font-heading uppercase tracking-wider bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white"
          data-testid="terrain-next-btn"
        >
          {submitting ? '...' : step === STEPS.length - 1
            ? (language === 'fr' ? 'Deployer' : 'Deploy')
            : (language === 'fr' ? 'Suivant' : 'Next')
          }
          {!submitting && <ChevronRight className="w-4 h-4 ml-1" />}
        </Button>
      </div>

      {/* Mental Module Banner — fixed at bottom */}
      <div className="w-full max-w-md mt-6" data-testid="terrain-mental-banner">
        <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5 text-center">
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-4 h-4 text-amber-400" />
            <p className="text-xs font-heading uppercase tracking-wider text-amber-400">
              {language === 'fr'
                ? 'Module de Renforcement Mental : Inclus et active par defaut pour toutes les missions.'
                : 'Mental Reinforcement Module: Included and active by default for all missions.'}
            </p>
          </div>
        </div>
      </div>

      {/* Skip */}
      <button
        onClick={() => onComplete(null)}
        className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="terrain-skip-btn"
      >
        {language === 'fr' ? 'Passer pour l\'instant' : 'Skip for now'}
      </button>
    </div>
  );
};

export default TerrainProfile;
